import {
  AlertTriangle,
  ClipboardCheck,
  FileText,
  FileVideo,
  Mail,
  MapPin,
  Scale,
  Users,
  ListChecks,
  Wrench,
  GitPullRequest,
  ShieldCheck,
} from "lucide-react";
import { SITE_CONFIG } from "@/config/constants";

export const heroContent = {
  heading:
    "Audyt dostępności, który kończy się planem naprawy\u00A0— nie raportem do szuflady.",
  body:
    "Badamy zgodność z WCAG 2.2 i Ustawą o dostępności cyfrowej. " +
    "Wynik: konkretne zadania dla IT, wsad do deklaracji i priorytety — co najpierw, co może poczekać.",
  primaryCta: {
    label: "Umów bezpłatną konsultację (20 min)",
    href: "#contact",
  },
  secondaryCta: {
    label: "Pobierz listę kontrolną (PDF)",
    href: "#lista-kontrolna",
  },
};

export const problemContent = {
  heading: "Znasz to?",
  description:
    "Kontrola albo pierwsza skarga od użytkownika — i nagle okazuje się, że deklaracja jest nieaktualna, IT nie wie co naprawić, a na odpowiedź masz 7 dni. " +
    "Lepiej mieć porządek zanim to nastąpi.",
  points: [
    {
      icon: AlertTriangle,
      title: "50+ kryteriów WCAG. Od czego w ogóle zacząć?",
      text: "Na co dzień nie ma czasu ustalić, które wymagania dotyczą Twojej strony, a które nie. Efekt: wszystko wydaje się pilne albo nic nie rusza do przodu.",
    },
    {
      icon: Scale,
      title: "Podmiot odpowiada — nawet jeśli stronę robiła zewnętrzna firma",
      text: `Prawo nie zna wyjątku „bo zleciliśmy firmie". Za dostępność i aktualność deklaracji odpowiada podmiot, nie wykonawca. Dotyczy instytucji publicznych i — od 2025 r. — również firm prywatnych.`,
    },
    {
      icon: FileText,
      title: "Deklaracja napisana rok temu? Pewnie już nie opisuje rzeczywistości.",
      text: "Nowa podstrona, formularz, film — każda zmiana na stronie to potencjalna niezgodność, o której deklaracja milczy.",
    },
    {
      icon: Users,
      title: "7 dni na odpowiedź. Bez procedury — improwizujesz.",
      text: "Przekroczysz termin, skarga trafia do Ministra Cyfryzacji. Stamtąd — nakaz dostosowania strony.",
    },
  ],
};

export const solutionContent = {
  heading: "Jak to wygląda w praktyce",
  description:
    "Badanie, lista zadań dla IT, deklaracja i procedura obsługi zgłoszeń. " +
    "Jedno zlecenie — temat zamknięty.",
  items: [
    {
      id: "audit",
      icon: ClipboardCheck,
      title: "Badamy stronę tak, jak korzysta z niej użytkownik",
      description:
        "Klawiatura, czytnik ekranu, narzędzia automatyczne. " +
        "Każde naruszenie WCAG 2.2 dostaje priorytet: co blokuje ludzi dziś, a co nie jest krytyczne.",
    },
    {
      id: "handoff",
      icon: ListChecks,
      title: "IT dostaje zadania, nie raport",
      description:
        "Gdzie jest błąd, jak go odtworzyć, co zmienić w kodzie, jak sprawdzić, " +
        `że działa. Żadnych ogólników w stylu „poprawić kontrast".`,
    },
    {
      id: "validation",
      icon: ShieldCheck,
      title: "Sprawdzamy, czy poprawki naprawdę działają",
      description:
        "Wracamy po wdrożeniu. Dostarczamy wsad do deklaracji dostępności " +
        "i schemat odpowiedzi na żądania — gotowe do publikacji.",
    },
  ],
};

export const whyUsContent = {
  heading: "Dlaczego BastunIT?",
  description:
    "Po naszym audycie IT wie co robić, a Ty masz dokumenty gotowe do publikacji. " +
    "Bez zgadywania, bez przeciągania tematu.",
  points: [
    {
      icon: GitPullRequest,
      title: "Testujemy ręcznie — nie tylko skanerem",
      text: "Automatyczne narzędzia łapią ok. 30% błędów. Resztę znajduje człowiek z klawiaturą i czytnikiem ekranu. Każdy nasz audyt obejmuje NVDA, nawigację klawiaturową i przepływy end-to-end.",
    },
    {
      icon: Wrench,
      title: "Opisujemy błędy językiem IT",
      text: "Lokalizacja w kodzie, kroki do odtworzenia, propozycja naprawy, kryterium odbioru. Programista nie musi zgadywać, o co chodzi.",
    },
    {
      icon: ShieldCheck,
      title: "Wracamy po wdrożeniu",
      text: "Raport bez weryfikacji to obietnica bez pokrycia. Sprawdzamy, czy poprawki działają w praktyce — i dopiero wtedy aktualizujemy deklarację.",
    },
  ],
  metrics: [
    {
      value: "7 dni",
      description: "ustawowy termin reakcji na żądanie zapewnienia dostępności",
    },
    {
      value: "2 miesiące",
      description: "maksymalny czas realizacji, gdy natychmiastowe działanie jest niemożliwe",
    },
    {
      value: "84",
      description: "skargi dotyczące dostępności cyfrowej w pierwszej połowie 2025 r. — dwa razy więcej niż rok wcześniej",
    },
    {
      value: "31 marca",
      description: "coroczny termin aktualizacji deklaracji dostępności — naruszenie bez badania strony",
    },
  ],
};

export const servicesContent = {
  heading: "Jak możemy pomóc",
  description:
    "Zakres zależy od sytuacji — pilna kontrola, bieżące utrzymanie " +
    "zgodności albo przygotowanie wymagań na nową stronę.",
  items: [
    {
      id: "audits",
      icon: ClipboardCheck,
      title: "Audyt dostępności strony lub aplikacji",
      description:
        "Badanie WCAG 2.2 poziomów A i AA: klawiatura, fokus, czytniki ekranu, kontrast, formularze i multimedia. " +
        "Na wyjściu — zadania dla IT z priorytetami.",
    },
    {
      id: "declarations",
      icon: FileText,
      title: "Deklaracja dostępności i jej aktualizacja",
      description:
        "Treść zgodna z wymaganiami Ministerstwa Cyfryzacji: poziom zgodności, " +
        "wyjątki, metoda przygotowania, dane kontaktowe. Doradzamy też, kiedy i jak aktualizować deklarację po zmianach na stronie.",
    },
    {
      id: "requests",
      icon: Scale,
      title: "Procedura obsługi żądań dostępności",
      description:
        "Schemat działania krok po kroku i szablony odpowiedzi na żądania, skargi i wnioski o alternatywę. " +
        "Wiesz, co zrobić i co wysłać — niezależnie od tego, kto się zgłosi.",
    },
    {
      id: "content",
      icon: FileVideo,
      title: "Dostępne treści i multimedia",
      description:
        "Napisy rozszerzone, transkrypcje, audiodeskrypcje, opisy alternatywne. " +
        "A jeśli tworzycie treści samodzielnie — doradzamy, jak pisać zrozumiale, zgodnie z WCAG.",
    },
  ],
};

export const ctaContent = {
  heading: "Nie wiesz od czego zacząć? Odezwij się.",
  description:
    "Wyślij adres strony — w 20 minut ustalimy, co wymaga uwagi.",
  primaryCta: {
    label: "Umów bezpłatną konsultację",
    href: "#contact",
  },
  secondaryCta: {
    label: "Napisz e-mail",
    href: `mailto:${SITE_CONFIG.email}`,
  },
};

export const contactContent = {
  heading: "Napisz do nas",
  description:
    "Czego dotyczy sprawa — strona, BIP, aplikacja? Co Cię niepokoi? " +
    "Odpiszemy i zaproponujemy kolejny krok.",
  highlights: [
    {
      label: "E-mail",
      value: SITE_CONFIG.email,
      icon: Mail,
      href: `mailto:${SITE_CONFIG.email}`,
    },
    { label: "Lokalizacja", value: SITE_CONFIG.location, icon: MapPin },
  ],
  form: {
    labels: {
      name: "Imię i nazwisko *",
      email: "E-mail *",
      phone: "Telefon",
      company: "Instytucja / firma",
      message: "Wiadomość *",
      consent: "Zgoda na przetwarzanie danych *",
    },
    placeholders: {
      name: "Jan Kowalski",
      email: "jan.kowalski@gmail.com",
      phone: "+48 500 300 700",
      company: "Nazwa instytucji / firmy",
      message:
        "Np. mamy stronę gminy i BIP, dostaliśmy pierwsze zgłoszenie od użytkownika i nie wiemy, od czego zacząć. Potrzebujemy audytu i pomocy z deklaracją.",
    },
    validation: {
      required: "To pole jest wymagane",
      minLength2: "Minimum 2 znaki",
      minLength10: "Minimum 10 znaków",
      maxLength100: "Maksymalnie 100 znaków",
      maxLength254: "Maksymalnie 254 znaki",
      maxLength30: "Maksymalnie 30 znaków",
      maxLength200: "Maksymalnie 200 znaków",
      maxLength5000: "Maksymalnie 5000 znaków",
      emailPattern: "Nieprawidłowy adres e-mail",
      phonePattern: "Nieprawidłowy numer telefonu",
      consent: "Zgoda jest wymagana",
    },
    consentTextStart:
      "Wyrażam zgodę na przetwarzanie moich danych osobowych zgodnie z ",
    consentLinkLabel: "Polityką Prywatności",
    consentLinkHref: "/polityka-prywatnosci",
    consentTextEnd: " w celu udzielenia odpowiedzi na moje zapytanie. *",
    submitIdle: "Wyślij wiadomość",
    submitPending: "Wysyłanie...",
    submitSuccess: "Dziękujemy za wiadomość! Skontaktujemy się wkrótce.",
    submitErrorGeneric: "Coś poszło nie tak. Spróbuj ponownie za chwilę.",
    submitErrorSummaryTitle: "Formularz zawiera błędy. Popraw wskazane pola:",
    submitErrorValidation: "Popraw błędy w formularzu i spróbuj ponownie.",
    requiredNote: "* Pola wymagane",
  },
};

export const caseStudyContent = {
  heading: "Przykład z praktyki",
  description:
    "Audyt ma sens, gdy ktoś wdroży poprawki. Oto co się zmieniło w 14 dni.",
  case: {
    label: "Audyt strony urzędu",
    institution: "Strona urzędu miasta, ok. 35 000 mieszkańców — strona główna i BIP",
    before: {
      heading: "Przed audytem",
      items: [
        "4 naruszenia WCAG poziomu A — krytyczne bariery dla czytników ekranu",
        "34 linki bez nazwy — czytnik ekranu nie mówił, dokąd prowadzą",
        "Deklaracja dostępności nieaktualna od 23 miesięcy",
        "Brak procedury na wypadek wniosku o dostępność",
      ],
    },
    after: {
      heading: "14 dni później",
      items: [
        "0 naruszeń krytycznych",
        "Deklaracja zaktualizowana i zgodna z ustawą",
        "Procedura wniosków wdrożona — 7-dniowy termin spełniony",
        "Redaktorzy CMS przeszkoleni z podstaw dostępności",
      ],
    },
    metrics: [
      { value: "14 dni", label: "od audytu do usunięcia naruszeń krytycznych" },
      { value: "38", label: "wykrytych błędów, w tym 4 blokujące czytniki ekranu" },
      { value: "23 mies.", label: "tyle deklaracja była przeterminowana — podstawa do skargi" },
    ],
  },
};

export const pricingContent = {
  heading: "Ile to kosztuje?",
  description:
    "Audyt poniesiesz raz. Bez niego każdy wniosek użytkownika to presja " +
    "7-dniowego terminu i ryzyko, które wraca przy każdej zmianie na stronie.",
  packages: [
    {
      id: "basic",
      name: "Audyt podstawowy",
      price: "od 1 500 PLN",
      priceNote: "+ VAT",
      description: "Wiesz, co naprawić i w jakiej kolejności.",
      items: [
        "Automatyczne i manualne badanie WCAG 2.2 (do 5 podstron)",
        "Raport z priorytetami i planem napraw",
        "Zadania dla IT — z lokalizacją błędu i propozycją naprawy",
        "Wsad do deklaracji dostępności",
        "Wyniki w 72h od uruchomienia badania",
      ],
      cta: "Umów konsultację",
      featured: false,
    },
    {
      id: "full",
      name: "Audyt z dokumentacją",
      price: "od 3 000 PLN",
      priceNote: "+ VAT",
      description: "Badanie, dokumenty i procedury. Jedno zlecenie zamyka temat zgodności.",
      items: [
        "Wszystko z audytu podstawowego",
        "Deklaracja dostępności (wzór gov.pl) gotowa do publikacji",
        "Weryfikacja poprawek po wdrożeniu",
        "Procedura obsługi żądań dostępności (Art. 14 Ustawy)",
        "Szablony odpowiedzi na wnioski i skargi — 7-dniowy termin",
      ],
      cta: "Umów konsultację",
      featured: true,
    },
    {
      id: "annual",
      name: "Utrzymanie dostępności",
      price: "od 5 200 PLN",
      priceNote: "/ rok + VAT",
      description: "Deklaracja aktualna cały rok — bez względu na to, ile zmian wejdzie na stronę.",
      items: [
        "4 przeglądy kwartalne",
        "Aktualizacja deklaracji po każdej istotnej zmianie",
        "Weryfikacja nowych treści i podstron",
        "Konsultacje e-mail bez limitu",
      ],
      cta: "Umów konsultację",
      featured: false,
    },
  ],
  note: "Ostateczna wycena zależy od złożoności serwisu. Wyniki audytu podstawowego w 72h. Bezpłatna konsultacja wstępna — 20 minut, bez zobowiązań.",
};

export const checklistPreviewContent = {
  id: "lista-kontrolna",
  heading: "5 błędów, które prawdopodobnie ma Twoja strona",
  description:
    "Monitoring Ministerstwa Cyfryzacji 2024: żadna ze 100 zbadanych stron publicznych nie była w pełni zgodna z ustawą. Oto co powtarzało się najczęściej.",
  items: [
    {
      rank: "01",
      stat: "94% stron",
      title: "Obrazy bez opisu alternatywnego",
      detail: "Osoba niewidoma słyszy nazwę pliku zamiast treści zdjęcia. Naruszenie WCAG 1.1.1 — Art. 5 Ustawy.",
    },
    {
      rank: "02",
      stat: "89% stron",
      title: "Przyciski i linki bez dostępnej nazwy",
      detail: `Menu hamburger, strzałki karuzeli, ikony social media — czytnik ekranu mówi „link" albo „przycisk". Nic więcej. WCAG 4.1.2.`,
    },
    {
      rank: "03",
      stat: "83% stron",
      title: "Formularze bez etykiet",
      detail: "Pole e-mail z samym placeholderem. Po kliknięciu placeholder znika — użytkownik nie wie, co wpisać. WCAG 1.3.1.",
    },
    {
      rank: "04",
      stat: "67% stron",
      title: "Brak skip linku",
      detail: "Kto nawiguje klawiaturą, musi na każdej podstronie przejść przez całe menu, żeby dotrzeć do treści. WCAG 2.4.1.",
    },
    {
      rank: "05",
      stat: "67% stron",
      title: "Niewystarczający kontrast kolorów",
      detail: "Szary tekst na białym tle. Nieczytelny dla osób słabowidzących i dla każdego w pełnym słońcu. WCAG 1.4.3.",
    },
  ],
  cta: {
    label: "Sprawdź moją stronę — bezpłatna konsultacja",
    href: "#contact",
  },
  pdfNote: "Pełna lista kontrolna (10 najczęstszych błędów + 5 dodatkowych kontroli) — do pobrania bezpłatnie w formacie PDF.",
  downloadForm: {
    heading: "Pobierz pełną listę kontrolną",
    description: "10 najczęstszych błędów + 5 dodatkowych kontroli — bezpłatnie w PDF.",
    emailLabel: "E-mail *",
    emailPlaceholder: "jan.kowalski@instytucja.pl",
    consentTextStart: "Wyrażam zgodę na przetwarzanie moich danych osobowych zgodnie z ",
    consentLinkLabel: "Polityką Prywatności",
    consentLinkHref: "/polityka-prywatnosci",
    consentTextEnd: " w celu przesłania listy kontrolnej. *",
    submitIdle: "Wyślij mi PDF",
    submitPending: "Wysyłanie...",
    submitSuccess: "Gotowe! Sprawdź skrzynkę — PDF już jest w drodze.",
    submitError: "Coś poszło nie tak. Spróbuj ponownie za chwilę.",
    emailError: "Nieprawidłowy adres e-mail",
    consentError: "Zgoda jest wymagana",
  },
};

export const footerContent = {
  company: {
    description:
      "Audyty dostępności cyfrowej, deklaracje i procedury obsługi zgłoszeń dla podmiotów publicznych i prywatnych w Polsce.",
  },
  quickLinks: {
    heading: "Szybkie linki",
    links: [
      { id: "quick-link-problem", label: "Problem", href: "#problem" },
      { id: "quick-link-solution", label: "Rozwiązanie", href: "#solution" },
      { id: "quick-link-services", label: "Usługi", href: "#services" },
      { id: "quick-link-pricing", label: "Cennik", href: "#pricing" },
      { id: "quick-link-contact", label: "Kontakt", href: "#contact" },
    ],
  },
  legal: {
    heading: "Informacje prawne",
    links: [
      { id: "privacy-policy", label: "Polityka prywatności", href: "/polityka-prywatnosci" },
      { id: "terms", label: "Regulamin", href: "/regulamin" },
      // Deklaracja dostępności tymczasowo ukryta — ustawa 2019 nie obowiązuje podmiotów prywatnych
      // { id: "accessibility-statement", label: "Deklaracja dostępności", href: "/deklaracja-dostepnosci" },
    ],
  },
  contact: {
    heading: "Kontakt",
  },
  bottomBar: {
    lines: [
      "© 2026 BastunIT. Wszystkie prawa zastrzeżone.",
      "Strona spełnia wymagania WCAG 2.2 na poziomie AA.",
    ],
    scrollTopLabel: "Wróć na górę",
  },
};